/**
 * ForceRefreshProvider Component
 * 
 * Wraps the app and shows a modal when force refresh is triggered.
 * Light QA Contract Compliant - Flag-gated, no impact when OFF.
 * 
 * @owner system-admin
 * @created 2025-12-09
 * @expiryDate 2026-03-09
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RefreshCw, Clock } from 'lucide-react';
import { useForceRefresh } from '@/hooks/useForceRefresh';
import { globalFeatureFlags } from '@/lib/featureFlags';

interface ForceRefreshProviderProps {
  children: React.ReactNode;
}

/**
 * Structured logging
 */
function logProvider(action: string, data?: Record<string, unknown>) {
  const logEntry = {
    timestamp: new Date().toISOString(),
    component: 'ForceRefreshProvider',
    action,
    ...data,
  };
  
  if (import.meta.env.DEV) {
    console.log('[ForceRefreshProvider]', logEntry);
  }
}

export function ForceRefreshProvider({ children }: ForceRefreshProviderProps) {
  const flagEnabled = globalFeatureFlags.isEnabled('ENABLE_FORCE_REFRESH');
  const { refreshPending, refreshEvent, dismissRefresh } = useForceRefresh();
  const [countdown, setCountdown] = useState<number>(10);
  const [isOpen, setIsOpen] = useState(false);
  
  // Handle refresh event - guard inside useEffect
  useEffect(() => {
    if (!flagEnabled) return;
    if (refreshPending && refreshEvent) {
      logProvider('modal_open', { event: refreshEvent });
      setIsOpen(true);
      setCountdown(refreshEvent.countdownSeconds || 10);
    }
  }, [flagEnabled, refreshPending, refreshEvent]);
  
  const handleReload = useCallback(() => {
    logProvider('reload_triggered', { manual: countdown > 0 });
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    window.location.reload();
  }, [countdown]);
  
  // Countdown timer - guard inside useEffect
  useEffect(() => {
    if (!flagEnabled || !isOpen || countdown <= 0) return;
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          logProvider('auto_reload', { countdown: 0 });
          handleReload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [flagEnabled, isOpen, countdown, handleReload]);
  
  const handleDismiss = useCallback(() => {
    logProvider('modal_dismissed');
    setIsOpen(false);
    dismissRefresh();
  }, [dismissRefresh]);
  
  // Conditional return AFTER all hooks
  if (!flagEnabled) {
    return <>{children}</>;
  }
  
  return (
    <>
      {children}
      
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary animate-spin" />
              Cập nhật phiên bản mới
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>{refreshEvent?.message || 'Ứng dụng cần được cập nhật phiên bản mới.'}</p>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  Tự động làm mới sau <strong className="text-foreground">{countdown}</strong> giây
                </span>
              </div>
              
              {refreshEvent?.triggeredBy && (
                <p className="text-xs text-muted-foreground">
                  Được kích hoạt bởi: {refreshEvent.triggeredBy}
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <button
              onClick={handleDismiss}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Để sau
            </button>
            <AlertDialogAction onClick={handleReload} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Làm mới ngay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
